import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import { StylesProvider } from '@mui/styles';
import React, { useState } from 'react';
import InterThin from '../Fonts/inter/ttf/Inter-Thin.ttf';
import InterExtraLight from '../Fonts/inter/ttf/Inter-ExtraLight.ttf';
import InterLight from '../Fonts/inter/ttf/Inter-Light.ttf';
import InterRegular from '../Fonts/inter/ttf/Inter-Regular.ttf';
import InterMedium from '../Fonts/inter/ttf/Inter-Medium.ttf';
import InterSemiBold from '../Fonts/inter/ttf/Inter-SemiBold.ttf';
import InterBold from '../Fonts/inter/ttf/Inter-Bold.ttf';
import InterExtraBold from '../Fonts/inter/ttf/Inter-ExtraBold.ttf';
import InterBlack from '../Fonts/inter/ttf/Inter-Black.ttf';

import { themeCreator } from './base';

/**
 * Application theme configuration.
 *
 * @name themes
 * @type {Theme}
 */
export const themes = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      ns: 400,
      sm: 600,
      ms: 700,
      md: 900,
      limit901: 901,
      tg: 1024,
      lg: 1200,
      xl: 1536,
      limit1600: 1600,
      xxl: 2000
    }
  },
  palette: {
    primary: {
      main: '#120242'
    },
    secondary: {
      main: '#25D3B1'
    },
    error: {
      main: '#F45252'
    },
    info: {
      main: '#F4A952'
    }
  },
  shape: {
    borderRadius: 12
  },
  general: {
    borderRadiusSm: '6px',
    borderRadiusMd: '8px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  minHeights: {
    sm: '100px',
    md: '200px',
    lg: '400px'
  },
  spacing: (value) => {
    if (typeof value === 'number') {
      return `${value * 8}px`; // Default: multiples of 8
    } else if (typeof value === 'string' && value.endsWith('px')) {
      return value; // Accept direct pixel values like '1px', '5px', etc.
    }
    throw new Error(
      'Invalid spacing value. Use a number or a string ending with "px".'
    );
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    custom: (props: any = {}) => ({
      fontWeight: props.fontWeight || 400,
      lineHeight: props.lineHeight || 1.5,
      letterSpacing: props.letterSpacing || '0.02em',
      textAlign: props.textAlign || 'left',
      color: props.color || 'inherit'
    })
  },
  components: {
    // Name of the component
    MuiDialog: {
      styleOverrides: {
        root: {
          borderRadius: '50px'
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          border: 0
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: '0px'
        },
        underline: {
          ':before': {
            borderBottom: 'none'
          },
          '::after': {
            borderBottom: 'none'
          },
          ':after': {
            borderBottom: 0
          }
        },
        input: {
          fontSize: '14px',
          background: 'var(--color-bg-subtle)'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter',
          fontSize: '14px',
          color: 'var(--color-text-grey-purple)',
          fontWeight: 400
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '4px'
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 700,
          fontSize: '12px',
          letterSpacing: '0.2px',
          color: 'rgba(0, 0, 0, 0.5)',
          margin: 0,
          fontFamily: 'Inter',
          lineHeight: '12px'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: 'var(--font-size-14)',
          fontWeight: 400,
          letterSpacing: '0.3px',
          textTransform: 'none',
          paddingTop: '13px',
          paddingBottom: '13px',
          borderRadius: '4px',
          fontFamily: 'Inter'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          fontFamily: 'Inter'
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter',
          fontWeight: 700,
          letterSpacing: 0.2
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Inter';
          src: url(${InterThin}) format('truetype');
          font-weight: 100;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterExtraLight}) format('truetype');
          font-weight: 200;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterLight}) format('truetype');
          font-weight: 300;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterRegular}) format('truetype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterMedium}) format('truetype');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterSemiBold}) format('truetype');
          font-weight: 600;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterBold}) format('truetype');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterExtraBold}) format('truetype');
          font-weight: 800;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter';
          src: url(${InterBlack}) format('truetype');
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }`
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          background: 'rgba(37, 211, 177, 0.2)',
          color: 'var(--color-secondary)',
          borderRadius: '12px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '20px',
          fontFamily: 'Inter'
        },
        standardError: {
          background: 'rgba(244, 82, 82, 0.2)',
          color: 'var(--color-error-alt)',
          borderRadius: '12px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '20px',
          fontFamily: 'Inter'
        }
      }
    }
  }
} as unknown as ThemeOptions);

export const ThemeContext = React.createContext(
  (themeName: string): void => {}
);

const ThemeProviderWrapper: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const curThemeName = localStorage.getItem('appTheme') || 'PureLightTheme';
  const [themeName, _setThemeName] = useState(curThemeName);
  const theme = themeCreator(themeName);

  const setThemeName = (themeName: string): void => {
    localStorage.setItem('appTheme', themeName);
    _setThemeName(themeName);
  };

  return (
    <ThemeProvider theme={theme}>
      <ThemeProvider theme={themes}>
        <StylesProvider injectFirst>
          <ThemeContext.Provider value={setThemeName}>
            {children}
          </ThemeContext.Provider>
        </StylesProvider>
      </ThemeProvider>
    </ThemeProvider>
  );
};
export default ThemeProviderWrapper;
